export type StatusPillStatus =
  | 'pass'
  | 'fail'
  | 'warn'
  | 'error'
  | 'info'
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'acknowledged'
  | 'active'
  | 'online'
  | 'offline'
  | 'paused'
  | 'disabled'
  | 'pending_first_heartbeat'
  | 'stale'
  | 'orphaned'
  | string

export interface StatusPillProps {
  status: StatusPillStatus
  size?: 'sm' | 'md'
}

const COLOR_MAP: Record<string, string> = {
  pass: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  // Liveness, NOT lifecycle. A fleet roster needs to say "is this reachable right now", which is
  // a different question from `active` (registered and not decommissioned) — and showing the two
  // in the same green was how a helmet never seen in its life read as healthy.
  online: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  fail: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  warn: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  // #359 — Agent staleness states. Amber so they sit between healthy
  // (green `active`) and explicitly broken (red `error`), matching the
  // existing AgentStatusCard amber badge palette.
  stale: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  orphaned: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  acknowledged: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  pending_first_heartbeat: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  disabled: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  offline: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  canceled: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
}

const FALLBACK = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'

export function StatusPill({ status, size = 'md' }: StatusPillProps) {
  const cls = COLOR_MAP[status] ?? FALLBACK
  const sizeCls = size === 'sm' ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-0.5 text-xs'
  const label = String(status).replace(/_/g, ' ')

  return <span className={`inline-flex items-center rounded-full font-medium ${cls} ${sizeCls}`}>{label}</span>
}
